import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { TrendingUp, Activity, BarChart3, Coins } from 'lucide-react';
import { useActor } from '../hooks/useActor';
import { useQuery } from '@tanstack/react-query';

interface MetricCard {
  title: string;
  value: string;
  change?: string;
  icon: React.ReactNode;
}

export default function CommunityMetricsView() {
  const { actor } = useActor();

  // Fetch real data
  const { data: assetStats, isLoading: assetStatsLoading } = useQuery({
    queryKey: ['assetStats'],
    queryFn: async () => {
      if (!actor) return null;
      return actor.getAssetStats(null);
    },
    enabled: !!actor,
    refetchInterval: 60000,
  });

  const { data: swapMetrics, isLoading: swapMetricsLoading } = useQuery({
    queryKey: ['swapMetrics'],
    queryFn: async () => {
      if (!actor) return null;
      return actor.getSwapMetrics(null);
    },
    enabled: !!actor,
    refetchInterval: 60000,
  });

  // Calculate metrics from real data
  const metrics: MetricCard[] = [
    {
      title: 'Total Assets Indexed',
      value: assetStatsLoading ? '...' : (assetStats?.totalAssets?.toString() || '0'),
      change: undefined,
      icon: <Coins className="h-5 w-5 text-primary" />,
    },
    {
      title: 'Asset Swap Volume',
      value: assetStatsLoading ? '...' : `$${assetStats?.totalVolume?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '0'}`,
      change: undefined,
      icon: <TrendingUp className="h-5 w-5 text-green-500" />,
    },
    {
      title: 'Active Assets',
      value: assetStatsLoading ? '...' : (assetStats?.activeAssets?.toString() || '0'),
      change: undefined,
      icon: <Activity className="h-5 w-5 text-blue-500" />,
    },
    {
      title: 'Total Swaps',
      value: swapMetricsLoading ? '...' : (swapMetrics?.totalSwaps?.toString() || '0'),
      change: undefined,
      icon: <BarChart3 className="h-5 w-5 text-purple-500" />,
    },
  ];


  return (
    <section className="py-16 px-4 bg-gradient-to-b from-transparent to-accent/5">
      <div className="container mx-auto max-w-7xl">
        <div className="mb-12 text-center">
          <h2 className="text-4xl font-bold bg-gradient-to-r from-primary via-purple-400 to-primary bg-clip-text text-transparent mb-4">
            Community Metrics
          </h2>
          <p className="text-muted-foreground text-lg">
            Real-time statistics and activity from KawSwap
          </p>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
          {metrics.map((metric, index) => (
            <Card key={index} className="border-2 border-border">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{metric.title}</CardTitle>
                {metric.icon}
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{metric.value}</div>
                {metric.change && (
                  <p className="text-xs text-muted-foreground mt-2">{metric.change}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

      </div>
    </section>
  );
}
